import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Linkedin, Link2 } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api from "../lib/api";
import { usePageMeta } from "../lib/seo";
import { SiteHeader, SiteFooter } from "../components/SiteChrome";
import { fmtDate } from "../lib/time";
import { Button } from "../components/ui/button";
import { PostCard, imgSrc } from "./Insights";

const SITE = "https://www.servantage.co.za";

export default function InsightArticle() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    window.scrollTo(0, 0);
    api.get(`/public/insights/${slug}`).then((r) => setD(r.data)).catch(() => setD({ missing: true }));
  }, [slug]);
  const p = d?.post;
  usePageMeta(p ? `${p.seo_title || p.title} | Servantage Insights` : "Insights | Servantage", `/insights/${slug}`, !!d?.missing, p ? {
    title: true, description: p.summary, image: p.cover_url || "/og-image.jpg", type: "article",
    jsonLd: {
      "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description: p.summary,
      image: p.cover_url ? `${SITE}${p.cover_url}` : `${SITE}/og-image.jpg`, datePublished: p.published_at, dateModified: p.updated_at,
      author: { "@type": "Person", name: p.author_name }, mainEntityOfPage: `${SITE}/insights/${p.slug}`,
      publisher: { "@type": "Organization", name: "Servantage", logo: { "@type": "ImageObject", url: `${SITE}/favicon.png` } },
    },
  } : {});

  const shareUrl = `${SITE}/api/public/share/insights/${slug}`;
  const copy = () => { navigator.clipboard?.writeText(shareUrl); toast.success("Share link copied"); };

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      {!d ? <p className="py-32 text-center text-slate-400">Loading…</p> : d.missing ? (
        <div className="py-32 text-center" data-testid="article-missing">
          <p className="font-head text-2xl font-bold text-slate-900">Article not found</p>
          <Link to="/insights" className="mt-4 inline-block font-semibold text-indigo-700">Back to Insights</Link>
        </div>
      ) : (
        <article className="pb-16" data-testid="insight-article">
          <header className="mx-auto max-w-3xl px-5 pt-10 sm:px-6 sm:pt-14">
            <Link to="/insights" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="article-back"><ArrowLeft className="h-4 w-4" /> All insights</Link>
            <Link to={`/insights?category=${encodeURIComponent(p.category)}`} className="mt-6 block text-xs font-bold uppercase tracking-[0.18em] text-indigo-600">{p.category}</Link>
            <h1 className="mt-3 font-head text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl" data-testid="article-title">{p.title}</h1>
            <p className="mt-4 text-lg text-slate-600">{p.summary}</p>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-slate-100 py-3 text-sm text-slate-500">
              <span><b className="text-slate-800">{p.author_name}</b> · {fmtDate(p.published_at)} · <Clock className="mb-0.5 inline h-3.5 w-3.5" /> {p.reading_minutes} min read</span>
              <span className="flex gap-2">
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="rounded-full border border-slate-200 p-2 hover:bg-slate-50" data-testid="share-linkedin"><Linkedin className="h-4 w-4" /></a>
                <button onClick={copy} aria-label="Copy share link" className="rounded-full border border-slate-200 p-2 hover:bg-slate-50" data-testid="share-copy"><Link2 className="h-4 w-4" /></button>
              </span>
            </div>
          </header>
          {p.cover_url && (
            <div className="mx-auto mt-8 max-w-5xl px-5 sm:px-6">
              <img src={imgSrc(p.cover_url)} alt={p.title} className="aspect-[16/9] w-full rounded-2xl object-cover" data-testid="article-cover" />
            </div>
          )}
          <div className="sv-prose mx-auto mt-10 max-w-3xl px-5 sm:px-6" data-testid="article-body"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.body_html.replaceAll('src="/api/', `src="${process.env.REACT_APP_BACKEND_URL}/api/`)) }} />
          <div className="mx-auto mt-12 max-w-3xl px-5 sm:px-6">
            <div className="rounded-2xl bg-[#1E1B4B] p-7 text-white sm:p-9">
              <p className="font-head text-2xl font-bold">See your frontline service in real numbers.</p>
              <p className="mt-2 text-indigo-200">Create a free Servantage workspace with demo data in under a minute.</p>
              <Link to="/register"><Button className="mt-5 bg-white text-indigo-900 hover:bg-indigo-50" data-testid="article-cta">Create your free workspace <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            </div>
          </div>
          {d.related.length > 0 && (
            <section className="mx-auto mt-16 max-w-6xl px-5 sm:px-6">
              <h2 className="font-head text-2xl font-bold text-slate-900">Related insights</h2>
              <div className="mt-6 grid gap-6 md:grid-cols-3">{d.related.map((r) => <PostCard key={r.id} p={r} />)}</div>
            </section>
          )}
        </article>
      )}
      <SiteFooter />
    </div>
  );
}
